import React, {useState, useEffect} from 'react';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {useParams} from 'react-router-dom';
import {Typography, Accordion, AccordionSummary, AccordionDetails, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import './BrokerSpreadsheetErrorReview.sass';
import ErrorDisplayAccordion from './ErrorDisplayAccordion';
import {useSelector, useDispatch} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getEnrollmentApplicationsDetail} from 'Enrollment/ducks/enrollmentFileProcessing';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import EmployeeValidationErrorSection from './EmployeeValidationErrorSection';
import {EmployeeErrorDetail} from 'Enrollment/models/EmployeeErrorDetail';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import BrokerSpreadsheetFileSummaryAccordion from './BrokerSpreadsheetFileSummaryAccordion';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import * as I from 'immutable';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

const BrokerSpreadsheetErrorReview = () => {
  const {fileId} = useParams();
  const dispatch = useDispatch();

  useBreadcrumbs('Error Review', EnrollmentRouteConstants.BROKER_SPREADSHEET_STATUS_ERROR_REVIEW(fileId!));
  const [expandAllErrorTypes, setExpandAllErrorTypes] = useState(false);
  const {enrollmentApplicationsDetails} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  
  useEffect(() => {
    dispatch(getEnrollmentApplicationsDetail(fileId!));
  }, [fileId]);

  const handleExpandOrCollapseAll = () => {
    setExpandAllErrorTypes(!expandAllErrorTypes);
  };

  const isFileUploadBefore = (enrollmentFile1: EnrollmentApplication, enrollmentFile2: EnrollmentApplication) =>
    new Date(enrollmentFile2.fileUploadedDate!).getTime() - new Date(enrollmentFile1.fileUploadedDate!).getTime();

  return renderAsyncView(enrollmentApplicationsDetails, details => {
    return (
      <div className="spreadsheet-error-review-section">
        {pipe(
          details,
          NEA.fromArray,
          O.map(enrollmentFiles => {
            const enrollments = enrollmentFiles.sort(isFileUploadBefore);
            const selectedEnrollmentFile = enrollments.find(enrollment => enrollment.id! === fileId) ?? NEA.head(enrollments);
            const enrollmentfileHistory = enrollments.filter(enrollment => enrollment.id! !== fileId) ?? NEA.tail(enrollments);
            const employeesByErrorType = selectedEnrollmentFile!
              .employeeErrorDetails!.map(employeeDetails =>
                employeeDetails!
                  .errors!.map(error => I.Map({[error]: employeeDetails}))
                  .reduce((acc, value) => acc.merge(value), I.Map({}))
              )
              .map(employeeErrors => employeeErrors.map(employee => [employee]))
              .reduce(
                (acc, value) => acc.mergeWith((oldValue, newValue) => [...oldValue, ...newValue], value),
                I.Map({})
              );

            return (
              <>
                <BrokerSpreadsheetFileSummaryAccordion
                  enrollmentFile={selectedEnrollmentFile}
                  enrollmentFileHistory={enrollmentfileHistory}
                  fileId={fileId!}
                />
                <Accordion className="summary-line-info__expansion-panel">
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    disabled={selectedEnrollmentFile.invalidRecordCount === 0}
                  >
                    <div className="summary-line-info__expansion-panel__icon-container file-errors">
                      <i className="icon-container__file-error-icon"></i>
                    </div>
                    <div className="summary-line-info__expansion-panel__file-details">
                      <div className="summary-line-info__expansion-panel__file-details--content">
                        <span className="title">Employee(s) Error Details</span>
                      </div>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails className="summary-line-info__expansion-panel-details">
                    {selectedEnrollmentFile.employeeErrorDetails?.map((employee: EmployeeErrorDetail, index: number) => {
                      return (
                        <div className="employee-error-details" key={`employee-error-details__${index}`}>
                          <div className="employee-error-details__header">
                            <div className="employee-error-details__header--field">
                              <span className="title">First Name:</span>
                              <span className="value">{employee.firstName}</span>
                            </div>
                            <div className="employee-error-details__header--field">
                              <span className="title">Last Name:</span>
                              <span className="value">{employee!.lastName}</span>
                            </div>
                          </div>
                          <Grid className="employee-error-details__content" container spacing={4}>
                            <EmployeeValidationErrorSection validationErrors={employee.errors!} />
                          </Grid>
                        </div>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>

                <Accordion className="summary-line-info__expansion-panel">
                  <AccordionSummary>
                    <div className="summary-line-info__expansion-panel__icon-container file-errors">
                      <i className="icon-container__file-error-icon"></i>
                    </div>

                    <div className="summary-line-info__expansion-panel__file-details">
                      <div className="summary-line-info__expansion-panel__file-details--content">
                        <span className="title">Employee(s) With Errors:</span>
                        <span className="value">{selectedEnrollmentFile.employeeErrorDetails?.length}</span>
                      </div>

                      <div className="summary-line-info__expansion-panel__file-details--content">
                        <span className="title">Error Types:</span>
                        <span className="value">{employeesByErrorType.toIndexedSeq().toArray().length}</span>
                      </div>
                    </div>
                  </AccordionSummary>
                </Accordion>

                <div className="spreadsheet-error-review-section__body">
                  <div className="spreadsheet-error-review-section__body--header">
                    <Typography className="spreadsheet-error-review-section__body--header-title" variant="h6">
                      Error Type Summary
                    </Typography>
                    <div className="spreadsheet-error-review-section__body--header-icon">
                      <ExpandMoreIcon
                        className={expandAllErrorTypes ? 'expand-less' : 'expand-more'}
                        onClick={handleExpandOrCollapseAll}
                        aria-label="expand-all"
                      />
                    </div>
                  </div>
                  {employeesByErrorType.toIndexedSeq().toArray().length > 0 ? (
                    employeesByErrorType
                      .map((errorDetails, errorType) => {
                        return (
                          <ErrorDisplayAccordion
                            expanded={expandAllErrorTypes}
                            errorType={errorType}
                            employeeWithError={errorDetails}
                            key={`spreadsheet-error-review-section__${toKebabCase(errorType)}`}
                          />
                        );
                      })
                      .toIndexedSeq()
                      .toArray()
                  ) : (
                    <div className=" enrollment-file-error-accordion__summary">No Error Details Available</div>
                  )}
                </div>
              </>
            );
          }),
          O.getOrElse(() => <div className="">No File Information Available</div>)
        )}
      </div>
    );
  });
};

export default BrokerSpreadsheetErrorReview;
